import React, { createContext, useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { baseColumnsDefaultConfigs } from './columns';
import { messages } from '../messages';
import { usePaginationStats } from '../../../../hooks/use-pagination-for-grid';
import { usePaging } from '../../../../hooks/use-paging';
import { bulkloadSalesOrders } from '../../../../store/bulkload/bulkload.actions';
import Global from '../../../../store/global-selectors';
import { getEnhancedReturns, getEnhancedReturnsSellerGrid } from '../../../../store/returns/returns.api';
import {
  ALL_STORES_STORE_GUID,
  useEnhancedReturnRouteMatch,
  useIsUserAbleToUseEnhancedReturns,
} from '../../../../store/returns/returns.helpers';
import { useIsShipstationReturnsEnabled } from '../../../../hooks/use-seller-setting';
import { useEnhancedReturnsCountContext } from '../../enhanced-returns-count-context';

export const EnhancedReturnsContext = createContext();
export const useEnhancedReturnsContext = () => React.useContext(EnhancedReturnsContext);

export const EnhancedReturnsContextProvider = ({ children, columnConfigForView = undefined }) => {
  const dispatch = useDispatch();
  const { storeGuid, statusViewId } = useEnhancedReturnRouteMatch();
  const userCanUseEnhancedReturns = useIsUserAbleToUseEnhancedReturns();
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);

  const [returns, setReturns] = useAsyncSafeState([]);
  const [totalRecords, setTotalRecords] = useAsyncSafeState(0);
  const [filters, setFilters] = useState({});
  const [columnsConfig, setColumnsConfig] = useState(columnConfigForView || baseColumnsDefaultConfigs);
  const [sortedColumn, setSortedColumn] = useState(columnsConfig.find((col) => col.sorted) ?? columnsConfig[0]);
  const { page, pageSize, updatePageInformation } = usePaging();
  const { totalPages, viewStartingCount, viewEndingCount } = usePaginationStats(totalRecords);
  const { fetchCountForStore, searchTerm, isSearchMode } = useEnhancedReturnsCountContext();
  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();
  const stores = useSelector(Global.selectSellerActiveStores);
  const store = stores.find((s) => s.storeGuid === storeGuid);
  // In SSR mode, treat setup as complete for all stores so we display the grid content
  // even if ShipStation has not yet received acknowledgement from SSR that the store's
  // basic activation was completed. Without this, the tab would remain empty for valid
  // SSR stores that haven't confirmed activation back to us.
  const isStoreSetupCompleteForEnhancedReturns = isShipstationReturnsEnabled ? true : store?.isStoreSetupCompleteForEnhancedReturns;
  const [isLoading, setIsLoading] = useAsyncSafeState(isStoreSetupCompleteForEnhancedReturns && userCanUseEnhancedReturns);

  const fetchReturns = useCallback(async () => {
    if (!isStoreSetupCompleteForEnhancedReturns || !userCanUseEnhancedReturns) {
      setReturns([]);
      setTotalRecords(0);
      setIsLoading(false);
      return;
    }

    const isAllStores = storeGuid === ALL_STORES_STORE_GUID;

    setIsLoading(true);

    try {
      const combinedFilters = {
        ...filters,
        orderBy: sortedColumn.id,
        orderByDirection: sortedColumn.sorted,
        searchTerm,
      };

      // Pass filtered store GUIDs if a store filter is active, otherwise null so the count context uses its default
      const filteredStoreGuids = filters.storeGuids || null;
      fetchCountForStore(storeGuid, searchTerm, filteredStoreGuids);

      const gridResponse = isAllStores
        ? await getEnhancedReturnsSellerGrid(statusViewId, page, pageSize, combinedFilters)
        : await getEnhancedReturns(storeGuid, statusViewId, page, pageSize, combinedFilters);
      setReturns(gridResponse.results);
      setTotalRecords(gridResponse.totalRecords);
      const salesOrderIds = gridResponse.results.map((row) => row.salesOrderGuid).filter((id) => id);
      // TODO: might consider an in memory cache to only bulk load if the salesOrderIds changed
      if (salesOrderIds.length > 0) {
        dispatch(bulkloadSalesOrders(salesOrderIds));
      }
    } catch {
      setReturns([]);
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: <FormattedMessage {...messages.fetchReturnsError} />,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    dispatch,
    fetchCountForStore,
    filters,
    isStoreSetupCompleteForEnhancedReturns,
    page,
    pageSize,
    searchTerm,
    setIsLoading,
    setReturns,
    setTotalRecords,
    sortedColumn.id,
    sortedColumn.sorted,
    statusViewId,
    storeGuid,
    userCanUseEnhancedReturns,
  ]);

  const onReload = useCallback(() => {
    fetchReturns();
  }, [fetchReturns]);

  const updateColumnsConfig = useCallback(
    (config) => {
      setColumnsConfig(config);
      setSortedColumn(config.find((col) => col.sorted) ?? config[0]);
    },
    [setColumnsConfig],
  );

  const updateFilters = useCallback(
    (newFilters) => {
      if (!shallowEqual(newFilters, filters)) {
        setFilters(newFilters);
        updatePageInformation(1, pageSize);
      }
    },
    [filters, pageSize, updatePageInformation],
  );

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  // TODO: ideally the best solution is to start hoisting up normalized returns data to redux or some other top level state
  // Order details is loaded from a Component near the top of the component tree
  // This allows us to render the order details page from anywhere in the app, via routing
  // Unfortunately, this means there's no clean way of passing in our onReload function to Order Details
  // This is the best workaround we can do where we reload the grid after we detect a change in the URL from an order details path to a non-order details path
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    if (currentPath !== previousPath && previousPath.includes('order') && !currentPath.includes('order')) {
      fetchReturns();
    }
    previousPathRef.current = currentPath;
  }, [fetchReturns, location.pathname]);

  const value = useMemo(
    () => ({
      columnsConfig,
      filters,
      isLoading,
      isSearchMode,
      isStoreSetupCompleteForEnhancedReturns,
      onReload,
      page,
      pageSize,
      returns,
      setIsLoading,
      sortedColumn,
      statusViewId,
      storeGuid,
      totalPages,
      totalRecords,
      updateColumnsConfig,
      updateFilters,
      updatePageInformation,
      viewEndingCount,
      viewStartingCount,
    }),
    [
      columnsConfig,
      filters,
      isLoading,
      isSearchMode,
      isStoreSetupCompleteForEnhancedReturns,
      onReload,
      page,
      pageSize,
      returns,
      setIsLoading,
      sortedColumn,
      statusViewId,
      storeGuid,
      totalPages,
      totalRecords,
      updateColumnsConfig,
      updateFilters,
      updatePageInformation,
      viewEndingCount,
      viewStartingCount,
    ],
  );

  return <EnhancedReturnsContext.Provider value={value}>{children}</EnhancedReturnsContext.Provider>;
};
